import React, { createContext, useState, useCallback, useMemo, useContext, useEffect, useRef } from 'react';
import * as api from '../services/apiService';
import type { Opportunity, Comment, SearchResult, Application, AttendeeEntry } from '../types';
import { useAuth } from './AuthProvider';
import { useUI } from './UIProvider';
import { useNavigation } from './NavigationProvider';

type OmittedFields = 'id' | 'author' | 'likes' | 'comments' | 'amplifies' | 'amplifiedBy' | 'timestamp' | 'applications' | 'status' | 'likedBy' | 'recentAmplifiers' | 'applicationCount';
type OpportunityCreationData = Omit<Opportunity, OmittedFields> & { authorHandle?: string };
type OpportunityUpdateData = Omit<Opportunity, OmittedFields>;

export interface Filters {
  type: string;
  roleNeeded: string;
  genre: string;
  instrument: string;
  city: string;
  region: string;
  country: string;
  paidOnly: boolean;
  author: string;
  minWage: string;
}

const DEFAULT_FILTERS: Filters = { 
    type: '',
    roleNeeded: '',
    genre: '',
    instrument: '',
    city: '',
    region: '',
    country: '',
    paidOnly: false,
    author: '', 
    minWage: '' 
};

const STORAGE_KEY = 'beyouramp-saved-filters';

interface OpportunitiesContextState {
  opportunities: Opportunity[];
  setOpportunities: React.Dispatch<React.SetStateAction<Opportunity[]>>;
  myApplications: { opportunity: Opportunity, application: Application }[];
  isSyncingApps: boolean;
  filters: Filters;
  displayItems: SearchResult[];
  comments: Comment[];
  setComments: React.Dispatch<React.SetStateAction<Comment[]>>;
  createOpportunity: (opportunityData: Omit<OpportunityCreationData, 'authorHandle'>) => Promise<Opportunity | void>;
  updateOpportunity: (opportunityId: string, opportunityData: OpportunityUpdateData) => Promise<Opportunity | void>;
  deleteOpportunity: (opportunityId: string) => Promise<void>;
  closeOpportunity: (opportunityId: string) => Promise<void>;
  createNewApplications: (opportunityId: string, data: { 
    applyingAs: { individual: boolean; bands: { id: string; name: string }[] }; 
    coverLetter: string; 
    submissionLinks: string[] 
  }) => Promise<void>;
  withdrawApplication: (applicationId: string, opportunityId: string) => Promise<void>;
  likeOpportunity: (opportunityId: string) => Promise<void>;
  amplifyOpportunity: (opportunityId: string) => Promise<void>;
  attendOpportunity: (opportunityId: string) => Promise<void>;
  handleManualFilterChange: (updater: React.SetStateAction<Filters>) => void;
  handleAiFilterUpdate: (query: string) => Promise<void>;
  acceptApplicant: (opportunityId: string, applicantId: string) => Promise<void>;
  rejectApplication: (opportunityId: string, applicantId: string) => Promise<void>;
  shortlistApplication: (opportunityId: string, applicantId: string) => Promise<void>;
  postComment: (opportunityId: string, text: string) => Promise<void>;
  refreshMyApplications: () => Promise<void>;
  refreshOpportunities: (isInitial?: boolean) => Promise<void>;
  saveCurrentFilters: () => void;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMoreOpportunities: () => Promise<void>;
}
const OpportunitiesContext = createContext<OpportunitiesContextState | null>(null);

export const useOpportunities = () => {
    const context = useContext(OpportunitiesContext);
    if (!context) throw new Error('useOpportunities must be used within an OpportunitiesProvider');
    return context;
};

export const OpportunitiesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser, managedProfile, authStatus, users } = useAuth();
    const { showToast } = useUI();
    const { view, viewingOpportunity, setViewingOpportunity, searchTerm, bands } = useNavigation();
    
    const [opportunities, setOpportunities] = useState<Opportunity[]>([]);
    const [myApplications, setMyApplications] = useState<{ opportunity: Opportunity, application: Application }[]>([]);
    const [isSyncingApps, setIsSyncingApps] = useState(false);
    const [comments, setComments] = useState<Comment[]>([]);
    
    const [filters, setFilters] = useState<Filters>(() => {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
            try {
                return { ...DEFAULT_FILTERS, ...JSON.parse(saved) };
            } catch (e) {
                return DEFAULT_FILTERS;
            }
        }
        return DEFAULT_FILTERS;
    });
    
    const [hasMore, setHasMore] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const lastDocIdRef = useRef<string | null>(null);

    const refreshOpportunities = useCallback(async (isInitial = true) => {
        if (isInitial) {
            setIsLoadingMore(true);
            lastDocIdRef.current = null;
        }
        try {
            const result = await api.getOpportunities(lastDocIdRef.current || undefined);
            if (isInitial) {
                setOpportunities(result.opportunities);
            } else {
                setOpportunities(prev => {
                   const existingIds = new Set(prev.map(o => o.id));
                   const uniqueNew = result.opportunities.filter(o => !existingIds.has(o.id));
                   return [...prev, ...uniqueNew];
                });
            }
            lastDocIdRef.current = result.lastDocId;
            setHasMore(result.hasMore);
        } catch (e) {
            console.error("Failed to refresh opportunities", e);
        } finally {
            setIsLoadingMore(false);
        }
    }, []);

    const refreshMyApplications = useCallback(async () => {
        if (!currentUser) return;
        setIsSyncingApps(true);
        try {
            const data = await api.getMyApplications();
            setMyApplications(data);
        } catch (e) {
            console.error("Failed to refresh my applications", e);
        } finally {
            setIsSyncingApps(false);
        }
    }, [currentUser]);

    const loadMoreOpportunities = useCallback(async () => {
        if (isLoadingMore || !hasMore) return;
        setIsLoadingMore(true);
        await refreshOpportunities(false);
    }, [isLoadingMore, hasMore, refreshOpportunities]);

    useEffect(() => {
        if (authStatus === 'authenticated' && currentUser && !currentUser.needsOnboarding) {
            refreshOpportunities(true);
            refreshMyApplications();
        }
    }, [authStatus, currentUser, refreshOpportunities, refreshMyApplications]);

    useEffect(() => {
        const isViewingOpp = view === 'opportunityDetail' || view === 'editOpportunity' || view === 'applicantList';
        if (isViewingOpp && viewingOpportunity) {
            api.getComments(viewingOpportunity.id).then(setComments).catch(console.error);
        } else {
            setComments([]);
        }
    }, [view, viewingOpportunity]);

    const displayItems = useMemo((): SearchResult[] => {
        const activeTier = managedProfile?.subscriptionTier || currentUser?.subscriptionTier;
        const isFan = activeTier === 'fan_basic';
        const fanAllowedTypes = ['Gig', 'Event', 'Jam Session', 'Live Show', 'Stage Play', 'Open Mic', 'DJ set', 'Stand Up Comedy'];

        const minWageNum = parseFloat(filters.minWage) || 0;
        const filteredOpps = opportunities.filter(opp => {
            if (isFan && !fanAllowedTypes.includes(opp.type)) return false;

            // 1. Opportunity Type
            if (filters.type && opp.type !== filters.type) return false;

            // 2. Opportunity For
            if (filters.roleNeeded && opp.roleNeeded !== filters.roleNeeded) return false;

            // 3. Genre check
            if (filters.genre) {
                if (!opp.genre) return false;
                if (!opp.genre.toLowerCase().includes(filters.genre.toLowerCase())) return false;
            }

            // 4. Instrument check
            if (filters.instrument) {
                if (!opp.instrument) return false;
                if (!opp.instrument.toLowerCase().includes(filters.instrument.toLowerCase())) return false;
            }

            // 5. City check (matches opp.location)
            if (filters.city && !opp.location.toLowerCase().includes(filters.city.toLowerCase())) return false;

            // 6. Region check
            if (filters.region && (!opp.region || !opp.region.toLowerCase().includes(filters.region.toLowerCase()))) return false;

            // 7. Country check
            if (filters.country && (!opp.country || !opp.country.toLowerCase().includes(filters.country.toLowerCase()))) return false;

            // 8. Paid Only check
            if (filters.paidOnly && !opp.salary) return false;
            
            // Legacy wage checks
            if (minWageNum > 0 && (!opp.salary || opp.salary.amount < minWageNum)) return false;
            
            // Legacy author check
            if (filters.author) {
                const authorSearch = filters.author.toLowerCase().replace('@', '');
                const nameMatch = opp.author.name.toLowerCase().includes(authorSearch);
                const handleMatch = opp.author.handle.toLowerCase().includes(authorSearch);
                if (!nameMatch && !handleMatch) return false;
            }

            return true;
        });

        const lowercasedTerm = searchTerm.toLowerCase().trim();
        if (!lowercasedTerm) return filteredOpps.map(item => ({ ...item, resultType: 'opportunity' as const }));

        const searchedUsers: SearchResult[] = users.filter(u => u.name.toLowerCase().includes(lowercasedTerm) || u.handle.toLowerCase().includes(lowercasedTerm)).map(u => ({ ...u, resultType: 'user' as const }));
        const searchedBands: SearchResult[] = bands.filter(b => b.name.toLowerCase().includes(lowercasedTerm)).map(b => ({ ...b, resultType: 'band' as const }));
        const searchedOpps: SearchResult[] = filteredOpps.filter(o => o.title.toLowerCase().includes(lowercasedTerm)).map(o => ({ ...o, resultType: 'opportunity' as const }));
        
        return [...searchedUsers, ...searchedBands, ...searchedOpps];
    }, [opportunities, filters, searchTerm, users, bands, currentUser, managedProfile]);

    const createOpportunity = async (opportunityData: any) => {
        try {
            const authorHandle = managedProfile ? managedProfile.handle : currentUser?.handle;
            await api.createOpportunity({ ...opportunityData, authorHandle });
            await refreshOpportunities(true);
            showToast('Opportunity posted!');
        } catch (error: any) { showToast(error.message); }
    };

    const updateOpportunity = async (opportunityId: string, opportunityData: any) => {
        try {
            await api.updateOpportunity(opportunityId, opportunityData);
            await refreshOpportunities(true);
            showToast('Updated!');
        } catch(e) { showToast('Failed to update.'); }
    };

    const deleteOpportunity = async (opportunityId: string) => {
        if (window.confirm('Delete this?')) {
            await api.deleteOpportunity(opportunityId);
            await refreshOpportunities(true);
        }
    };
    
    const closeOpportunity = async (opportunityId: string) => {
        await api.closeOpportunity(opportunityId);
        await refreshOpportunities(true);
    };
    
    const createNewApplications = async (opportunityId: string, data: any) => {
        try {
            await api.createApplication(opportunityId, data);
            await Promise.all([
                refreshOpportunities(true),
                refreshMyApplications()
            ]);
            showToast('Application submitted!');
        } catch (error: any) {
            showToast(error.message);
        }
    };

    const withdrawApplication = async (applicationId: string, opportunityId: string) => {
        await api.deleteApplication(opportunityId, applicationId);
        await Promise.all([
            refreshOpportunities(true),
            refreshMyApplications()
        ]);
    };

    const likeOpportunity = async (opportunityId: string) => {
        try { await api.likeOpportunity(opportunityId); await refreshOpportunities(true); } catch(e) {}
    };
    
    const amplifyOpportunity = async (opportunityId: string) => {
        try { await api.amplifyOpportunity(opportunityId); await refreshOpportunities(true); } catch(e) {}
    };

    const attendOpportunity = async (opportunityId: string) => {
        try {
            await api.attendOpportunity(opportunityId);
            // Optimistic update so the counter reflects immediately without waiting for a full refresh
            if (viewingOpportunity && viewingOpportunity.id === opportunityId) {
                const effectiveHandle = managedProfile?.handle ?? currentUser?.handle;
                const activeUser = managedProfile ?? currentUser;
                if (effectiveHandle && activeUser) {
                    const currentAttendees: AttendeeEntry[] = viewingOpportunity.attendees || [];
                    const isCurrentlyAttending = currentAttendees.some(a => a.handle === effectiveHandle);
                    const newAttendees = isCurrentlyAttending
                        ? currentAttendees.filter(a => a.handle !== effectiveHandle)
                        : [...currentAttendees, { handle: effectiveHandle, name: activeUser.name, avatarUrl: activeUser.avatarUrl, timestamp: new Date().toISOString() }];
                    const currentCount = viewingOpportunity.attendeeCount ?? currentAttendees.length;
                    setViewingOpportunity({ ...viewingOpportunity, attendees: newAttendees, attendeeCount: currentCount + (isCurrentlyAttending ? -1 : 1) });
                }
            }
            refreshOpportunities(true);
        } catch(e: any) { showToast(e.message || 'Failed to update attendance.'); }
    };

    const postComment = async (opportunityId: string, text: string) => {
        await api.postComment(opportunityId, text);
        setComments(await api.getComments(opportunityId));
    };

    const handleManualFilterChange = (updater: any) => setFilters(updater);
    const handleAiFilterUpdate = async (query: string) => {
        const aiFilters = await api.processNaturalLanguageSearchQuery(query);
        setFilters(prev => ({ ...prev, ...aiFilters }));
    };

    const saveCurrentFilters = useCallback(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filters));
        showToast("Filters saved as default.");
    }, [filters, showToast]);

    const acceptApplicant = async (oId: string, aId: string) => { await api.acceptApplicant(oId, aId); await refreshOpportunities(true); };
    const rejectApplication = async (oId: string, aId: string) => { await api.rejectApplication(oId, aId); await refreshOpportunities(true); };
    const shortlistApplication = async (oId: string, aId: string) => { await api.shortlistApplicant(oId, aId); await refreshOpportunities(true); };

    const value = { 
        opportunities, setOpportunities, myApplications, isSyncingApps, filters, displayItems, comments, setComments, 
        createOpportunity, updateOpportunity, deleteOpportunity, closeOpportunity, 
        createNewApplications, withdrawApplication, likeOpportunity, amplifyOpportunity, attendOpportunity,
        handleManualFilterChange, handleAiFilterUpdate, acceptApplicant, rejectApplication, 
        shortlistApplication, postComment, refreshMyApplications, refreshOpportunities, 
        saveCurrentFilters, hasMore, isLoadingMore, loadMoreOpportunities
    };

    return <OpportunitiesContext.Provider value={value}>{children}</OpportunitiesContext.Provider>;
};